import { DatePipe, Location } from "@angular/common";
import { Component, OnInit, ViewChild, ViewEncapsulation } from "@angular/core";
import { NgForm } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { Observable, Subject, throwError } from "rxjs";
import { finalize, map } from "rxjs/operators";
import { GroupsEnum } from "./../../enum/groups.enum";
import { InvestedFund } from "./../../model/domain/InvestedFund";

import { AccountManager } from "../../model/domain/AccountManager";
import { Fund } from "../../model/domain/Fund";
import { Group } from "../../model/domain/Group";
import { User } from "../../model/domain/User";
import { AuthService } from "../../services/auth.service";
import { LoaderService } from "../../services/loader.service";
import { SecurityService } from "../../services/security.service";
import { SnackbarService } from "../../services/snackbar.service";
import { TicketService } from "../../services/ticket.service";
import { UserService } from "../../services/user.service";
import { ValidatorService } from "../../services/validator.service";
import { Roles } from "../auth/auth.role";
import { AuthToken } from "../auth/auth.token";

@Component({
  selector: "app-cadastro-usuario",
  templateUrl: "./cadastro-usuario.component.html",
  styleUrls: ["./cadastro-usuario.component.css"],
  encapsulation: ViewEncapsulation.None,
})
export class CadastroUsuarioComponent implements OnInit {
  @ViewChild("thisForm", { static: true }) public frm: NgForm;

  model: User = null;
  canValidate = false;
  isEditing = false;
  groups: Group[] = null;
  userGroup = GroupsEnum;
  distributorGroups: Group[] = null;
  investedFund: InvestedFund[] = null;
  accountManagers: AccountManager[] = null;
  auth: AuthToken = null;
  gestora: AccountManager = null;
  nomeGestora: string = null;
  fundsByName: Observable<Fund[]>;
  fundsByDocument: Observable<Fund[]>;
  fundSearchNameTerm = new Subject<string>();
  fundSearchDocumentTerm = new Subject<string>();
  isSending = false;
  isEmailSent = false;

  constructor(
    private service: UserService,
    public validator: ValidatorService,
    private snackBarService: SnackbarService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private securityService: SecurityService,
    private authService: AuthService,
    private datePipe: DatePipe,
    private ticketService: TicketService,
    private loaderService: LoaderService
  ) {
    this.auth = authService.getAuth();
  }

  validators = {
    accountManager: () =>
      this.model.isExternalUser
        ? this.model.accountManagerId != null &&
          this.model.accountManagerId != 0
        : true,
    userDocument: (value) => this.validator.validatePhysicalDocument(value),
  };

  ngOnInit() {
    this.getUser();

    this.ticketService.getInvestedFunds().subscribe((f) => {
      this.investedFund = f;
    });

    this.securityService.GetAccountManagers().subscribe((a) => {
      this.accountManagers = a;
    });

    this.securityService
      .GetAccountManager(this.authService.authToken.username)
      .subscribe((g) => {
        if (g != null) {
          this.gestora = g;
          this.nomeGestora = g.document + " - " + g.name;
          this.model.accountManagerId = g.id;
        }
      });

    if (this.canCreateMaster()) {
      this.securityService.GetGroups().subscribe((g) => {
        this.distributorGroups = this.groups = g;
      });
    } else {
      this.securityService
        .GetMasterGroupsByUsername(this.authService.authToken.username)
        .subscribe((g) => {
          this.distributorGroups = this.groups = g;
        });
    }
  }

  getDistributorGroups() {
    this.groups = [];
    if (this.model.isDistributor) {
      this.model.isExternalUser = false;

      this.groups = this.distributorGroups.filter(
        (x) =>
          x.id == GroupsEnum.DistribuidorMaster ||
          x.id == GroupsEnum.DistribuidorBoleta
      );
    } else {
      this.groups = this.distributorGroups;
    }
  }

  getUser() {
    this.model = new User();
    const id = +this.route.snapshot.paramMap.get("id");
    this.model.id = id;

    if (id) {
      this.service.GetUser(id).subscribe(
        (q) => {
          Object.assign(this.model, q);

          this.model.passwordExpirationDate = this.datePipe.transform(
            this.model.passwordExpirationDate,
            "dd/MM/yyyy"
          ) as any;

          if (this.model.userDocument) {
            this.model.userDocument = this.validator.formatDocument(
              this.model.userDocument
            );
          }

          if (this.model.lockoutEndDateUtc) {
            this.model.lockoutEndDateUtc = this.datePipe.transform(
              this.model.lockoutEndDateUtc,
              "dd/MM/yyyy"
            ) as any;
          }

          if (this.model.isDistributor) {
            this.model.isExternalUser = true;
          }

          this.isEditing = true;
          this.canValidate = true;
        },
        () => this.router.navigate(["/cadastro-usuario"])
      );
    }
  }

  send() {
    this.loaderService.addLoader();
    this.isSending = true;

    this.authService
      .passwordResetByAdmin(this.model.userName)
      .pipe(
        finalize(() => {
          this.isSending = false;
          this.loaderService.removeLoader();
        })
      )
      .subscribe((result) => {
        if (result) {
          this.snackBarService.open("E-mail de reset de senha enviado.", "OK");
          return;
        }
        this.snackBarService.open("Erro ao resetar a senha.", "OK");
      });
  }

  showResetPassword() {
    return this.authService.hasGroup([this.userGroup.AdministradorSistema]);
  }

  submit() {
    const editing = this.isEditing;

    this.register()
      .pipe(
        map((user: User) => {
          if (!editing) {
            this.model.id = user.id;

            this.isEditing = true;
            const url = this.router.createUrlTree([this.model.id], {
              relativeTo: this.route,
            });
            this.location.go(url.toString());
          }

          return this.model;
        })
      )
      .subscribe(
        (r) => {
          const msg = editing ? "Usuário alterado." : "Usuário cadastrado.";

          this.snackBarService.open(msg, "OK", {
            verticalPosition: "top",
            horizontalPosition: "right",
          });
        },
        (result) => {
          let err = result;
          if (result.status === 409) {
            err = "Esse login já existe em nossa base!";
          } else if (result.message) {
            err = result.message;
          }

          this.snackBarService.open(err, "OK", {
            verticalPosition: "top",
            horizontalPosition: "right",
          });
        }
      );
  }

  register() {
    const editing = this.isEditing;

    Object.values(this.frm.controls).forEach((c) => {
      c.markAsTouched();
      c.updateValueAndValidity();
    });

    this.canValidate = true;

    const errors = this.getErrors();

    if (this.frm.invalid || errors.length > 0) {
      return throwError("Verifique os campos destacados.");
    }

    const data = Object.assign({}, this.model);

    if (!data.isExternalUser) data.accountManagerId = null;

    data.userDocument = this.validator.sanitize(data.userDocument);
    data.id = null;

    // if (!this.canCreateMaster()) {
    //   data.accountManagerId = this.gestora.id;
    // }

    if (data.lockoutEndDateUtc != null) {
      data.lockoutEndDateUtc = new Date(
        data.lockoutEndDateUtc
          .toString()
          .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
      );
    }

    if (data.passwordExpirationDate != null) {
      data.passwordExpirationDate = new Date(
        data.passwordExpirationDate
          .toString()
          .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
      );
    }

    const method = editing
      ? this.service.UpdateUser(this.model.id, data)
      : this.service.RegisterUser(data);

    location.reload;
    return method;
  }

  coalesce(value: any) {
    return value || "";
  }

  hasError(prop: string) {
    if (!this.canValidate) {
      return false;
    }

    return !this.validators[prop](this.model[prop]);
  }

  getErrors(): string[] {
    const validationErrors = [];

    Object.keys(this.validators).forEach((k) => {
      if (!this.validators[k](this.model[k])) {
        validationErrors.push(k);
      }
    });

    return validationErrors;
  }

  canCreateMaster() {
    const roles = [Roles.CreateMasterUser];
    return this.authService.hasRoles(roles);
  }

  canCreateSubordinate() {
    const roles = [Roles.CreateSubordinateUser];
    return this.authService.hasRoles(roles);
  }

  canCreateDistributor() {
    const roles = [Roles.CreateDistributorUser];
    var distributorRole = this.authService.hasRoles(roles);
    if (distributorRole) {
      this.model.isDistributor = true;
    }
    return distributorRole;
  }

  isReadonly() {
    return this.model.id ? true : false;
  }

  reset2FA() {
    this.authService
      .reset2FAForUser(this.model.id)      
      .subscribe((result) => {
        if (result) {
          this.snackBarService.open("MFA resetado.", "OK");
          return;
        }
        this.snackBarService.open("Erro ao resetar o MFA.", "OK");
      });
  }
}
